/**
 * Campo "Famiglia" per il form materiale.
 *
 * Logica:
 *  - Trova il supplier_id dal nome testo `formData.supplier` (match case-insensitive)
 *  - Se trovato → mostra Select con le famiglie di quel supplier + opzione "+ Nuova famiglia"
 *  - Se non trovato → disabilita Select con hint "Scegli prima un fornitore valido"
 *  - Inline CreateFamilyDialog: alla creazione, auto-seleziona la nuova famiglia
 *
 * Salvataggio: il valore selezionato finisce in `formData.family_id` → poi
 * propagato a `materials.family_id` da `useMaterialSubmit`.
 */
import React, { useMemo, useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Layers, Plus } from 'lucide-react';
import {
  useSuppliersList,
  useSupplierFamilies,
} from '@/hooks/useSupplierManagement';
import CreateFamilyDialog from '@/components/suppliers/CreateFamilyDialog';
import type { MaterialFormData } from './MaterialFormData';

interface MaterialFamilyFieldProps {
  formData: MaterialFormData;
  setFormData: React.Dispatch<React.SetStateAction<MaterialFormData>>;
  isSubmitting?: boolean;
}

const ADD_NEW_SENTINEL = '__add_new_family__';
const NONE_SENTINEL = '__none__';

const MaterialFamilyField: React.FC<MaterialFamilyFieldProps> = ({
  formData,
  setFormData,
  isSubmitting,
}) => {
  const { data: suppliers = [] } = useSuppliersList();

  // Match fornitore testo → record suppliers (case-insensitive)
  const supplierMatch = useMemo(() => {
    const name = (formData.supplier ?? '').trim().toLowerCase();
    if (!name) return null;
    return suppliers.find((s: { name: string }) => s.name.toLowerCase() === name) ?? null;
  }, [formData.supplier, suppliers]);

  const supplierId = (supplierMatch as { id?: string } | null)?.id ?? null;
  const supplierName = (supplierMatch as { name?: string } | null)?.name ?? null;

  const { data: families = [] } = useSupplierFamilies(supplierId);
  const [showAddDialog, setShowAddDialog] = useState(false);

  // Family corrente in lista
  const currentFamilyId = formData.family_id ?? '';

  const handleChange = (value: string) => {
    if (value === ADD_NEW_SENTINEL) {
      setShowAddDialog(true);
      return;
    }
    if (value === NONE_SENTINEL) {
      setFormData((prev) => ({ ...prev, family_id: null }));
      return;
    }
    setFormData((prev) => ({ ...prev, family_id: value || null }));
  };

  return (
    <div>
      <Label htmlFor="family" className="flex items-center gap-1">
        <Layers className="h-3 w-3" />
        Famiglia di prodotto
      </Label>
      <Select
        value={currentFamilyId || NONE_SENTINEL}
        onValueChange={handleChange}
        disabled={isSubmitting || !supplierId}
      >
        <SelectTrigger id="family">
          <SelectValue
            placeholder={
              supplierId
                ? 'Seleziona o aggiungi famiglia…'
                : 'Inserisci un fornitore valido per scegliere la famiglia'
            }
          />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_SENTINEL}>
            <span className="text-muted-foreground">— Nessuna famiglia</span>
          </SelectItem>
          {families.length > 0 && (
            <div className="px-2 py-1 text-[10px] uppercase tracking-wider text-muted-foreground border-t border-b my-1">
              {supplierName ?? 'Famiglie'}
            </div>
          )}
          {families.map((f: { id: string; code: string; name: string }) => (
            <SelectItem key={f.id} value={f.id}>
              <span className="font-mono text-[10px] mr-1.5 text-muted-foreground">{f.code}</span>
              {f.name}
            </SelectItem>
          ))}
          {supplierId && (
            <div className="border-t mt-1 pt-1">
              <SelectItem value={ADD_NEW_SENTINEL} className="text-primary font-medium">
                <Plus className="h-3 w-3 mr-1 inline" />
                Aggiungi nuova famiglia…
              </SelectItem>
            </div>
          )}
        </SelectContent>
      </Select>
      {supplierId && (
        <p className="text-[10px] text-muted-foreground mt-1">
          Lo sconto cumulativo della famiglia (definito in <strong>Fornitori → {supplierName}</strong>)
          si applicherà automaticamente al prezzo netto di questo materiale.
        </p>
      )}

      {/* Dialog: nuova famiglia (auto-seleziona al create) */}
      {supplierId && (
        <CreateFamilyDialog
          supplierId={supplierId}
          supplierName={supplierName ?? undefined}
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          onCreated={(family) =>
            setFormData((prev) => ({ ...prev, family_id: family.id }))
          }
        />
      )}
    </div>
  );
};

export default MaterialFamilyField;
